import { createClient } from '@supabase/supabase-js'

type MockSupabaseUser = {
  id: string
  email: string
  user_metadata?: Record<string, any>
  created_at: string
}

type MockSupabaseSession = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  expires_at: number
  user: MockSupabaseUser
}

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
const isProduction = process.env.NODE_ENV === 'production'

function createMockSession(user: MockSupabaseUser | null): MockSupabaseSession | null {
  if (!user) {
    return null
  }

  const expiresIn = 60 * 60 // 1 hour
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn

  return {
    access_token: 'mock-access-token',
    refresh_token: 'mock-refresh-token',
    token_type: 'bearer',
    expires_in: expiresIn,
    expires_at: expiresAt,
    user
  }
}

function createMockSupabaseClient() {
  console.warn('[Supabase] Missing environment variables. Using mock Supabase client for development/testing.')

  let currentUser: MockSupabaseUser | null = null
  const listeners: Array<(event: string, session: MockSupabaseSession | null) => void> = []

  const buildUser = (email: string, name?: string): MockSupabaseUser => ({
    id: `mock-user-${Math.random().toString(36).slice(2, 10)}`,
    email,
    user_metadata: {
      name: name || email.split('@')[0] || 'Test User',
      is_premium: false
    },
    created_at: new Date().toISOString()
  })

  const notify = (event: string) => {
    const session = createMockSession(currentUser)
    listeners.forEach(listener => {
      try {
        listener(event, session)
      } catch (error) {
        console.error('Mock Supabase listener error:', error)
      }
    })
  }

  const auth = {
    async signInWithPassword({ email, password }: { email: string; password: string }) {
      if (!email || !password) {
        return {
          data: { user: null, session: null },
          error: { message: 'Invalid login credentials' }
        }
      }

      currentUser = buildUser(email)
      const session = createMockSession(currentUser)

      notify('SIGNED_IN')

      return {
        data: { user: currentUser, session },
        error: null
      }
    },

    async signUp({ email, password, options }: { email: string; password: string; options?: any }) {
      if (!email || !password) {
        return {
          data: { user: null, session: null },
          error: { message: 'Email and password are required' }
        }
      }

      const name = options?.data?.name
      currentUser = buildUser(email, name)
      const session = createMockSession(currentUser)

      notify('SIGNED_IN')

      return {
        data: { user: currentUser, session },
        error: null
      }
    },

    async signOut() {
      currentUser = null
      notify('SIGNED_OUT')
      return { error: null }
    },

    async getUser() {
      return {
        data: { user: currentUser },
        error: null
      }
    },

    async getSession() {
      return {
        data: { session: createMockSession(currentUser) },
        error: null
      }
    },

    async refreshSession() {
      return {
        data: { session: createMockSession(currentUser) },
        error: null
      }
    },

    async updateUser({ data }: { data?: Record<string, any>; password?: string }) {
      if (currentUser) {
        currentUser = {
          ...currentUser,
          user_metadata: {
            ...currentUser.user_metadata,
            ...data
          }
        }

        notify('USER_UPDATED')
      }

      return {
        data: { user: currentUser },
        error: null
      }
    },

    async resetPasswordForEmail() {
      return { data: {}, error: null }
    },

    onAuthStateChange(callback: (event: string, session: MockSupabaseSession | null) => void) {
      listeners.push(callback)

      // Immediately emit current state
      callback(currentUser ? 'SIGNED_IN' : 'SIGNED_OUT', createMockSession(currentUser))

      return {
        data: {
          subscription: {
            unsubscribe() {
              const index = listeners.indexOf(callback)
              if (index !== -1) {
                listeners.splice(index, 1)
              }
            }
          }
        },
        error: null
      }
    }
  }

  return { auth } as any
}

export const supabase = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'ielts-auth'
      },
      global: {
        headers: {
          'X-Client-Info': 'ielts-writing-practice@6.0.0'
        }
      }
    })
  : (() => {
      if (isProduction) {
        console.error('Supabase URL:', supabaseUrl ? 'Set' : 'Missing')
        console.error('Supabase Anon Key:', supabaseAnonKey ? 'Set' : 'Missing')
        throw new Error('Missing Supabase environment variables. Please check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY')
      }

      return createMockSupabaseClient()
    })()

// Helper function to get current session
export async function getSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) {
    console.error('Error getting session:', error)
    return null
  }
  return data?.session ?? null
}

// Helper function to get current user
export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser()
  if (error) {
    console.error('Error getting user:', error)
    return null
  }
  return data?.user ?? null
}

// Helper function to sign out
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) {
    console.error('Error signing out:', error)
    throw error
  }
}

// Export types for TypeScript
export type { Session, User, AuthError } from '@supabase/supabase-js'

